import { formatLongDate, startOfDay } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Collapse, InputAdornment } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { useTu } from "@src/appV2/i18n/useTu";
import { getServerNow } from "@src/appV2/lib/clockOffset";
import { useState } from "react";

import { Icon } from "../../components/Icon";
import { TextInput } from "../../components/TextInput";
import { LabeledField } from "./LabeledField";

interface ShiftDateFieldProps {
  value: Date | undefined;
  onChange: (date: Date) => void;
  timezone: string;
}

/**
 * "Date" field for the Add Shift panel. Shows the selected calendar date and
 * expands an inline month calendar underneath when clicked.
 */
export function ShiftDateField(props: ShiftDateFieldProps) {
  const { tu } = useTu("schedule");
  const { value, onChange, timezone } = props;
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const minDate = startOfDay(getServerNow(), { timeZone: timezone });

  const toggleCalendar = () => {
    setIsCalendarOpen((previous) => !previous);
  };

  return (
    <LabeledField label={tu("Date")}>
      <TextInput
        fullWidth
        size="small"
        value={isDefined(value) ? formatLongDate(value) : ""}
        placeholder={tu("Select a date")}
        inputProps={{
          "aria-label": tu("Date"),
          readOnly: true,
          sx: { cursor: "pointer" },
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggleCalendar();
            }
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Icon
                type="calendar"
                size="small"
                sx={{ color: "text.secondary", cursor: "pointer" }}
              />
            </InputAdornment>
          ),
        }}
        onClick={toggleCalendar}
      />
      <Collapse unmountOnExit in={isCalendarOpen}>
        <DateCalendar
          sx={{
            // deliberately shrinking the DateCalendar component to make it fit on the side panel
            width: "100%",
            maxWidth: 320,
            "& .MuiDayCalendar-weekDayLabel, & .MuiPickersDay-root": {
              marginX: 0,
            },
            "& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer": {
              justifyContent: "space-between",
            },
          }}
          minDate={minDate}
          value={value}
          onChange={(date: Date | null) => {
            if (isDefined(date)) {
              onChange(date);
              setIsCalendarOpen(false);
            }
          }}
        />
      </Collapse>
    </LabeledField>
  );
}
